import { hasModifierKey } from '@angular/cdk/keycodes';
import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subscription, filter, map, merge, mergeMap, switchMap, tap } from 'rxjs';

import { resolveAnalyticsPage, sanitizeAnalyticsName } from '../directives/app-analytics.utils';
import { PostHogService } from './posthog.service';

type ModalCloseReason = 'code' | 'backdrop' | 'escape' | 'navigation';
type ModalDismissReason = Extract<ModalCloseReason, 'backdrop' | 'escape'>;

const MODAL_NAME_ATTRIBUTE = 'data-analytics-modal';
const CONTAINER_SELECTOR = 'mat-dialog-container';
const UNKNOWN_MODAL = 'unknown';
const SELECTOR_AFFIXES = /^app-|-(dialog|modal)$/g;

interface ModalIdentity {
  modal_name: string;
  modal_component?: string;
  page: string;
}

@Injectable({ providedIn: 'root' })
export class ModalTrackingService implements OnDestroy {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private ngZone = inject(NgZone);
  private posthogService = inject(PostHogService);

  private subscription?: Subscription;

  start(): void {
    if (this.subscription) {
      return;
    }
    this.subscription = this.dialog.afterOpened.pipe(mergeMap((ref) => this.track(ref))).subscribe();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private track(ref: MatDialogRef<unknown>): Observable<unknown> {
    const page = resolveAnalyticsPage(this.router);
    const openedAt = Date.now();
    let dismissReason: ModalDismissReason | null = null;

    return ref.afterOpened().pipe(
      map(() => this.identify(ref, page)),
      tap((identity) => this.posthogService.capture('modal_seen', { ...identity })),
      switchMap((identity) =>
        merge(
          this.dismissals(ref).pipe(
            tap((reason) =>
              this.runAfterComponentDismissHandlers(() => {
                if (ref.getState() !== MatDialogState.OPEN) {
                  dismissReason = reason;
                } else {
                  this.posthogService.capture('modal_dismiss_attempt', { ...identity, dismiss_reason: reason });
                }
              }),
            ),
          ),
          ref.beforeClosed().pipe(
            tap(() => {
              const closedByNavigation = ref.getState() === MatDialogState.OPEN;
              const timeOpenMs = Date.now() - openedAt;
              this.runAfterDismissReasonSettles(() => {
                const closeReason: ModalCloseReason = closedByNavigation ? 'navigation' : (dismissReason ?? 'code');
                this.posthogService.capture('modal_closed', {
                  ...identity,
                  close_reason: closeReason,
                  time_open_ms: timeOpenMs,
                });
              });
            }),
          ),
        ),
      ),
    );
  }

  private runAfterComponentDismissHandlers(callback: () => void): void {
    this.ngZone.runOutsideAngular(() => queueMicrotask(callback));
  }

  private runAfterDismissReasonSettles(callback: () => void): void {
    this.ngZone.runOutsideAngular(() => setTimeout(callback));
  }

  private dismissals(ref: MatDialogRef<unknown>): Observable<ModalDismissReason> {
    return merge(
      ref.backdropClick().pipe(map((): ModalDismissReason => 'backdrop')),
      ref.keydownEvents().pipe(
        filter((event) => event.key === 'Escape' && !hasModifierKey(event)),
        map((): ModalDismissReason => 'escape'),
      ),
    );
  }

  private identify(ref: MatDialogRef<unknown>, page: string): ModalIdentity {
    const host: HTMLElement | undefined = ref.componentRef?.location.nativeElement;
    const component = host?.tagName.toLowerCase();
    const container = host?.closest(CONTAINER_SELECTOR) ?? this.document.getElementById(ref.id);
    const explicitName = container?.querySelector(`[${MODAL_NAME_ATTRIBUTE}]`)?.getAttribute(MODAL_NAME_ATTRIBUTE);
    return {
      page,
      ...(component && { modal_component: component }),
      modal_name: sanitizeAnalyticsName(explicitName ?? component?.replace(SELECTOR_AFFIXES, '')) || UNKNOWN_MODAL,
    };
  }
}
